import * as React from 'react';
import User from '../../../../models/user';
import Scrollbars from 'react-custom-scrollbars';
import Table from '../../../table/table';
import TableHead from '../../../table/table-head';
import HeaderCell from '../../../table/table-header-cell';
import TableBody from '../../../table/table-body';
import * as styles from './client-users.module.scss';
import ClientUserEntry from './client-user-entry';
import { CircularProgress } from '@material-ui/core';
import useTimedAlerts from '../../../../hooks/useTimedAlerts';
import PriorityTwoFailure from '../../../announcement/priority-two-failure';
import { ClientUserSortingOptions } from '../../../../models/enums/adminSortingOptions';
import useClientUser from '../../../../hooks/useClientUser';
import Administrator from '../../../../models/administrator';
import { AssignedUser } from '../../../../models/client';

interface Props {
  assignedUsers: AssignedUser[];
  assignedAdministrators: Administrator[];
}

interface Sort {
  sortColumn: string;
  direction: 'asc' | 'desc';
}

/**
 * ClientUsersTable - fetch individual user data and display table of current client's users
 */
const ClientUsersTable = ({ assignedUsers, assignedAdministrators }: Props) => {
  const [loading, setLoading] = React.useState<boolean>(true);
  const [users, setUsers] = React.useState<User[]>(null);
  const [sort, setSort] = React.useState<Sort>({ sortColumn: ClientUserSortingOptions.Name, direction: 'asc' });
  const { errorMessage, setErrorMessage } = useTimedAlerts();
  const { userSort, fetchUsersDetails } = useClientUser();

  React.useEffect(() => {
    const allUsers = [...assignedUsers, ...assignedAdministrators];
    if (allUsers.length > 0) {
      fetchUsersDetails(allUsers)
        .then(users => {
          setUsers(userSort(users, sort.sortColumn, sort.direction));
          setLoading(false);
        })
        .catch(err => {
          setErrorMessage(err);
          setLoading(false);
        });
    } else {
      setUsers([]);
      setLoading(false);
    }
  }, []);

  const reorderHandler = (orderColumn: string, orderDirection: 'asc' | 'desc') => {
    setUsers(userSort(users, orderColumn, orderDirection));
    setSort({ sortColumn: orderColumn, direction: orderDirection });
  };

  return (
    <div className={styles.container}>
      {errorMessage && <PriorityTwoFailure message={errorMessage} />}
      <div className={styles.header}>
        <h1>Users</h1>
      </div>
      {loading ? (
        <div className={styles.circularProgress}>
          <CircularProgress />
        </div>
      ) : (
        <div className={styles.content}>
          <div>
            <span className={styles.resultsCount}>{users.length} users</span>
          </div>
          <Scrollbars className={styles.scrollTable} autoHide={true} universal={true}>
            <Table>
              <TableHead>
                <HeaderCell
                  title="Name"
                  scope="col"
                  orderField={ClientUserSortingOptions.Name}
                  orderColumn={sort.sortColumn}
                  orderDirection={sort.direction}
                  reorderHandler={reorderHandler}
                />
                <HeaderCell
                  title="User Type"
                  scope="col"
                  orderField={ClientUserSortingOptions.UserType}
                  orderColumn={sort.sortColumn}
                  orderDirection={sort.direction}
                  reorderHandler={reorderHandler}
                />
                <HeaderCell
                  title="Email"
                  scope="col"
                  orderField={ClientUserSortingOptions.Email}
                  orderColumn={sort.sortColumn}
                  orderDirection={sort.direction}
                  reorderHandler={reorderHandler}
                />
                <HeaderCell
                  title="Company"
                  scope="col"
                  orderField={ClientUserSortingOptions.Department}
                  orderColumn={sort.sortColumn}
                  orderDirection={sort.direction}
                  reorderHandler={reorderHandler}
                />
                <HeaderCell
                  title="Status"
                  scope="col"
                  orderField={ClientUserSortingOptions.Status}
                  orderColumn={sort.sortColumn}
                  orderDirection={sort.direction}
                  reorderHandler={reorderHandler}
                />
                <HeaderCell title="" />
              </TableHead>
              <TableBody>
                {!users || users.length == 0 ? (
                  <tr>
                    <td>No users are assigned to this client</td>
                  </tr>
                ) : (
                  users.map((user: User, index: number) => {
                    return <ClientUserEntry key={index} user={user} />;
                  })
                )}
              </TableBody>
            </Table>
          </Scrollbars>
        </div>
      )}
    </div>
  );
};

export default ClientUsersTable;
